import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";

// Import and expose build version for cache verification
import { APP_BUILD_VERSION } from "@/config/version";

// ============== CACHE BUSTING: UNREGISTER OLD SERVICE WORKERS ==============
// This ensures users always get the latest app bundle after deployments
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      // Keep Firebase messaging SW, unregister any others that might cache app assets
      if (registration.active && !registration.active.scriptURL.includes('firebase-messaging-sw.js')) {
        registration.unregister().then(() => {
          console.log('[CacheBust] Unregistered non-Firebase service worker:', registration.active.scriptURL);
        });
      }
    });
  }).catch(err => {
    console.log('[CacheBust] Service worker check failed:', err);
  });
}

// Log build version prominently for debugging
console.log(`%c[VeriTrade Build] ${APP_BUILD_VERSION}`, 'background: #f97316; color: white; padding: 4px 8px; font-size: 14px; font-weight: bold;');

// ============== GLOBAL ERROR HANDLERS (iOS SAFETY) ==============
// Catch unhandled errors that could cause blank pages

function _detectBrowserEnv() {
  try {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isFB = /FBAN|FBAV/.test(ua);
    const isIG = /Instagram/.test(ua);
    const isSocial = isFB || isIG || /Twitter|LinkedInApp|BytedanceWebview|TikTok/.test(ua);
    if (isFB) return isIOS ? 'ios_facebook_webview' : 'facebook_webview';
    if (isIG) return isIOS ? 'ios_instagram_webview' : 'instagram_webview';
    if (isSocial) return isIOS ? 'ios_social_webview' : 'social_webview';
    if (isIOS) return 'ios_safari';
    return 'standard';
  } catch { return 'unknown'; }
}

// Global error handler for synchronous errors
window.onerror = function(message, source, lineno, colno, error) {
  const browserEnv = _detectBrowserEnv();
  console.error('[GlobalError] Uncaught error:', {
    message,
    source,
    lineno,
    colno,
    browserEnv,
    error: error?.stack || error
  });
  
  // Try to report to backend (non-blocking)
  try {
    const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    fetch(`${API}/api/log-client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'window.onerror',
        message,
        source,
        lineno,
        colno,
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        browserEnv: _detectBrowserEnv(),
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch (e) {}
  
  // Return false to not prevent default handling
  return false;
};

// Global handler for unhandled promise rejections
window.onunhandledrejection = function(event) {
  console.error('[GlobalError] Unhandled promise rejection:', event.reason);
  
  // Try to report to backend (non-blocking)
  try {
    const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    fetch(`${API}/api/log-client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'unhandledrejection',
        reason: event.reason?.toString?.() || String(event.reason),
        stack: event.reason?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        browserEnv: _detectBrowserEnv(),
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch (e) {}
};

// ============== RENDER APP WITH ERROR PROTECTION ==============

/**
 * Render fallback UI if React completely fails to mount
 * This is a last resort for catastrophic failures
 */
function renderFallbackUI(error) {
  const root = document.getElementById('root');
  if (!root) return;
  
  root.innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f9fafb;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <div style="
        max-width: 400px;
        width: 100%;
        background-color: #ffffff;
        border-radius: 12px;
        padding: 32px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        text-align: center;
      ">
        <div style="
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background-color: #fef2f2;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        ">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h1 style="font-size: 20px; font-weight: 600; color: #111827; margin: 0 0 12px;">
          Unable to Load
        </h1>
        <p style="font-size: 14px; color: #6b7280; margin: 0 0 24px; line-height: 1.5;">
          We're having trouble loading the app. Please try refreshing or check back later.
        </p>
        ${_detectBrowserEnv().includes('webview') ? `
        <div style="background:#1e293b; color:#f1f5f9; border-radius:10px; padding:14px 16px; margin:0 0 16px; text-align:left; font-size:13px; line-height:1.5;">
          <p style="margin:0 0 6px; font-weight:600; color:#f97316; font-size:13px;">Tip: Open in Safari</p>
          <p style="margin:0; color:#94a3b8; font-size:12px;">Tap <b style="color:#f1f5f9">&#8943;</b> (menu) &rarr; <b style="color:#f1f5f9">Open in Safari</b></p>
        </div>
        ` : ''}
        <button onclick="window.location.reload()" style="
          width: 100%;
          padding: 12px 20px;
          background-color: #f97316;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        ">
          Refresh Page
        </button>
        <a href="/" style="
          display: block;
          margin-top: 12px;
          color: #6b7280;
          font-size: 14px;
          text-decoration: underline;
        ">
          Go to Homepage
        </a>
      </div>
    </div>
  `;
}

/**
 * Safe app initialization with full error protection
 */
function initializeApp() {
  try {
    const rootElement = document.getElementById("root");
    
    if (!rootElement) {
      console.error('[Init] Root element not found');
      return;
    }
    
    const root = ReactDOM.createRoot(rootElement);
    
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );
    
    console.log('[Init] App mounted successfully');
  } catch (error) {
    console.error('[Init] Failed to initialize app:', error);
    renderFallbackUI(error);
  }
}

// Start the app
initializeApp();
